import React, { useState, useEffect } from "react";
import { X, Save, Trash2, CheckSquare, Square, Printer, RefreshCw } from "lucide-react";
import { masterService } from "../api/masterService";

export default function SplitReceiptModal({ isOpen, onClose, data, onSave, onDelete }) {
  const [name, setName] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinterMac, setSelectedPrinterMac] = useState("");
  const [selectedPrinterName, setSelectedPrinterName] = useState("");
  const [printerWidth, setPrinterWidth] = useState("58mm");
  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState("");
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  const isChecker = data?.id === "checker";

  useEffect(() => {
    if (data) {
      setName(data.name || "");
      if (data.categories === "ALL") {
        setSelectedCategories("ALL");
      } else {
        setSelectedCategories(data.categories || []);
      }
      setSelectedPrinterMac(data.printerMac || "");
      setSelectedPrinterName(data.printerName || "");
      setPrinterWidth(data.printerWidth || "58mm");
    }
  }, [data]);

  useEffect(() => {
    fetchCategories();
    scanPrinters();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const res = await masterService.categories.list({ page: 1, limit: 100 });
      let list = [];
      if (res.data?.data && Array.isArray(res.data.data)) {
        list = res.data.data;
      } else if (res.data && Array.isArray(res.data)) {
        list = res.data;
      }
      setCategories(list);
    } catch (error) {
      console.error("Gagal mengambil kategori:", error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const scanPrinters = () => {
    if (!window.bluetoothSerial) {
      setScanStatus("Printer Bluetooth hanya tersedia di aplikasi Android.");
      return;
    }
    
    setIsScanning(true);
    setScanStatus("Mencari printer Bluetooth...");
    
    const startScanning = () => {
      window.bluetoothSerial.list(
        (list) => {
          setPrinters(list);
          setIsScanning(false);
          setScanStatus("");
        },
        (err) => {
          setScanStatus("Gagal mencari perangkat: " + err);
          setIsScanning(false);
        }
      );
    };

    if (window.cordova?.plugins?.permissions) {
      const permissions = window.cordova.plugins.permissions;
      permissions.requestPermissions(
        ["android.permission.BLUETOOTH_CONNECT", "android.permission.BLUETOOTH_SCAN"],
        startScanning,
        startScanning // fallback
      );
    } else {
      startScanning();
    }
  };

  const toggleCategory = (id) => {
    if (selectedCategories === "ALL") {
      // If it was ALL, switch to array mode and remove the clicked one
      const allIds = categories.map((c) => c.id);
      setSelectedCategories(allIds.filter((cid) => cid !== id));
      return;
    }

    const current = [...selectedCategories];
    if (current.includes(id)) {
      setSelectedCategories(current.filter((cid) => cid !== id));
    } else {
      setSelectedCategories([...current, id]);
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert("Nama nota pesanan tidak boleh kosong");
      return;
    }
    
    // Get category names for preview display
    let catNames = [];
    if (selectedCategories !== "ALL" && categories.length > 0) {
      catNames = selectedCategories
        .map(id => categories.find(c => c.id === id)?.name)
        .filter(Boolean);
    }

    onSave({
      id: data?.id,
      name,
      categories: selectedCategories,
      categoryNames: catNames,
      printerMac: selectedPrinterMac,
      printerName: selectedPrinterName,
      printerWidth: printerWidth,
    });
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl"
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800">
            {data?.id && data.id !== Date.now().toString()
              ? "Edit Mapping Kategori"
              : "Tambah Mapping Kategori"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Nama Nota */}
          <div>
            <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-2">
              Nama Nota Pesanan <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Contoh: Nota Dapur"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-teal-500 focus:outline-none transition-all"
            />
          </div>

          {/* Kategori Dicetak */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                Kategori Dicetak
              </label>
              {categories.length > 0 && (
                <div className="flex gap-2">
                  <button 
                    type="button"
                    onClick={() => {
                      if (isChecker) {
                        setSelectedCategories("ALL");
                      } else {
                        setSelectedCategories(categories.map((c) => c.id));
                      }
                    }}
                    className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold transition-colors cursor-pointer"
                  >
                    Checklist Semua
                  </button>
                  <span className="text-[10px] text-slate-300">|</span>
                  <button 
                    type="button"
                    onClick={() => setSelectedCategories([])}
                    className="text-[10px] text-red-500 hover:text-red-600 font-semibold transition-colors cursor-pointer"
                  >
                    Unchecklist Semua
                  </button>
                </div>
              )}
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-2 max-h-48 overflow-y-auto">
              {isLoadingCategories ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  Memuat kategori...
                </div>
              ) : categories.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  Belum ada kategori tersedia.
                </div>
              ) : (
                categories.map((cat) => {
                  const isSelected =
                    selectedCategories === "ALL" ||
                    selectedCategories.includes(cat.id);
                  return (
                    <div
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                        isSelected ? "bg-teal-50/50" : "hover:bg-slate-100"
                      }`}
                    >
                      <span
                        className={`text-sm ${
                          isSelected
                            ? "font-semibold text-teal-700"
                            : "text-slate-600"
                        }`}
                      >
                        {cat.name}
                      </span>
                      {isSelected ? (
                        <CheckSquare className="w-5 h-5 text-teal-600" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-300" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-2 italic">
              *Pilih kategori item yang akan dicetak di nota pesanan ini. Jika tidak ada kategori yang dipilih, nota ini tidak akan dicetak.
            </p>
          </div>

          {/* Printer Tujuan Cetak */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                Printer Tujuan Cetak
              </label>
              <button
                onClick={scanPrinters}
                disabled={isScanning}
                className="text-[10px] flex items-center gap-1 font-semibold text-teal-600 hover:text-teal-700 active:scale-95 transition-transform"
              >
                <RefreshCw className={`w-3 h-3 ${isScanning ? "animate-spin" : ""}`} />
                Scan Ulang
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mb-3">
              Pilih printer yang akan digunakan untuk mencetak nota ini.
            </p>

            {scanStatus && (
              <div className="text-[10px] bg-orange-50 text-orange-600 p-2 rounded mb-3">
                {scanStatus}
              </div>
            )}

            <div className="space-y-2">
              {/* Default option (if not selected) */}
              <div
                onClick={() => {
                  setSelectedPrinterMac("");
                  setSelectedPrinterName("");
                }}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  !selectedPrinterMac
                    ? "border-teal-500 bg-teal-50/30"
                    : "border-slate-200 hover:border-teal-200"
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    !selectedPrinterMac ? "border-teal-500" : "border-slate-300"
                  }`}
                >
                  {!selectedPrinterMac && (
                    <div className="w-2 h-2 rounded-full bg-teal-500" />
                  )}
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                  <Printer className="w-4 h-4 text-slate-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">
                    Printer Kasir (Bawaan)
                  </h4>
                  <p className="text-[10px] text-slate-500">
                    Menggunakan printer utama yang terhubung di kasir.
                  </p>
                </div>
              </div>

              {/* Bluetooth printers list */}
              {printers.map((p) => {
                const isSelected = selectedPrinterMac === p.address;
                return (
                  <div
                    key={p.address}
                    onClick={() => {
                      setSelectedPrinterMac(p.address);
                      setSelectedPrinterName(p.name);
                    }}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected
                        ? "border-teal-500 bg-teal-50/30"
                        : "border-slate-200 hover:border-teal-200"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? "border-teal-500" : "border-slate-300"
                      }`}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-teal-500" />
                      )}
                    </div>
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                      <Printer className="w-4 h-4 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">
                        {p.name}
                      </h4>
                      <p className="text-[10px] text-slate-500">
                        Bluetooth &middot; {p.address}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ukuran Kertas Printer (Hanya Android) */}
          <div className="pt-2">
            <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-2">
              Ukuran Kertas Printer
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPrinterWidth("58mm")}
                className={`flex-1 py-2 rounded-lg ring-1 text-sm font-semibold transition-colors ${
                  printerWidth === "58mm"
                    ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20"
                    : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"
                }`}
              >
                58mm
              </button>
              <button
                type="button"
                onClick={() => setPrinterWidth("80mm")}
                className={`flex-1 py-2 rounded-lg ring-1 text-sm font-semibold transition-colors ${
                  printerWidth === "80mm"
                    ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20"
                    : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"
                }`}
              >
                80mm
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              *Hanya berlaku untuk cetak Bluetooth (Android). Pada Windows, ukuran mengikuti konfigurasi utama.
            </p>
          </div>
        </div>

        <div className="p-5 border-t border-slate-100 flex justify-between gap-3">
          {!isChecker && data?.id && (
            <button
              onClick={() => {
                if (window.confirm("Apakah Anda yakin ingin menghapus mapping ini?")) {
                  onDelete(data.id);
                  onClose();
                }
              }}
              className="px-4 py-2.5 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Hapus
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
            >
              Batal
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-colors shadow-sm shadow-teal-500/30"
            >
              Simpan Konfigurasi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
